
import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import AnimatedButton from './AnimatedButton';

const PricingSection = () => {
  const navigate = useNavigate();

  const handleSignup = () => {
    navigate('/signup');
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="pricing" className="py-16 md:py-24 relative">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-blue-dark">
            Planos para todos os perfis
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Escolha o plano ideal para o seu negócio e potencialize seus resultados com nossas ferramentas especializadas.
          </p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
        >
          {/* Plano Grátis */}
          <motion.div variants={cardVariants} className="flex">
            <Card className="border-gray-300 shadow-md flex flex-col w-full hover:shadow-lg">
              <CardHeader className="pb-4">
                <div className="flex justify-center mb-4">
                  <div className="bg-gray-200 p-3 rounded-full">
                    <Check className="h-6 w-6 text-gray-700" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-center text-gray-700">Grátis</CardTitle>
                <CardDescription className="text-center">
                  <span className="block text-xl font-bold mt-2 text-blue-dark">GRATUITO</span>
                  <span className="text-sm text-gray-500">Para sempre</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Análise básica de imóveis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Visualização limitada de dados</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Dashboard simplificado</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="pt-4 flex justify-center">
                <AnimatedButton 
                  onClick={handleSignup}
                  className="w-full max-w-[200px] bg-gray-300 hover:bg-gray-400 text-gray-800 "
                >
                  Começar Agora
                </AnimatedButton>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Plano Básico */}
          <motion.div variants={cardVariants} className="flex">
            <Card className="border-blue-main shadow-md flex flex-col w-full relative hover:shadow-lg">
              <div className="absolute -top-4 left-0 right-0 flex justify-center">
                <span className="bg-blue-main text-white text-sm px-3 py-1 rounded-full">
                  Recomendado
                </span>
              </div>
              <CardHeader className="pb-4">
                <div className="flex justify-center mb-4">
                  <div className="bg-blue-light/20 p-3 rounded-full">
                    <Star className="h-6 w-6 text-blue-main" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-center text-blue-dark">Básico</CardTitle>
                <CardDescription className="text-center">
                  <span className="block text-xl font-bold mt-2 text-blue-dark">EM BREVE</span>
                  <span className="text-sm text-gray-500">Por mês</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Tudo do plano Grátis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Análises completas de imóveis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Gráficos e estatísticas avançadas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Comparativo de mercado</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="pt-4 flex justify-center">
                <AnimatedButton 
                  onClick={handleSignup}
                  className="w-full max-w-[200px] bg-blue-main hover:bg-blue-dark text-white"
                >
                  Inscreva-se
                </AnimatedButton>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Plano Premium */}
          <motion.div variants={cardVariants} className="flex">
            <Card className="border-blue-dark shadow-md flex flex-col w-full hover:shadow-lg">
              <CardHeader className="pb-4">
                <div className="flex justify-center mb-4">
                  <div className="bg-blue-dark/20 p-3 rounded-full">
                    <Award className="h-6 w-6 text-blue-dark" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-center text-blue-dark">Premium</CardTitle>
                <CardDescription className="text-center">
                  <span className="block text-xl font-bold mt-2 text-blue-dark">EM BREVE</span>
                  <span className="text-sm text-gray-500">Por mês</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Tudo do plano Básico</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Acesso a todos os dashboards</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Análise de imobiliárias</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Relatórios personalizados</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-blue-main" />
                    <span className="text-gray-700">Suporte prioritário</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="pt-4 flex justify-center">
                <AnimatedButton 
                  onClick={handleSignup}
                  className="w-full max-w-[200px] bg-blue-dark hover:bg-blue-darker text-white"
                >
                  Inscreva-se
                </AnimatedButton>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
